// src/RootComponent.js
import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useParams,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
  Navigate,
} from "react-router-dom";
import { LoadingOverlay, Box, Text, Center, Stack, ThemeIcon, ActionIcon, MantineProvider, Modal, Button, Radio, Global } from "@mantine/core";
import InitialLoadingScreen from "./components/initial-loading-screen";
import { IconShieldX, IconX } from "@tabler/icons-react";
import App from "./App";
import { DataProvider } from "./context/DataContext";
import NotFound from "./pages/not-found";
import SubRoutes from "./routes";
import {
  FingerprintProvider,
  Fingerprint,
} from "@fingerprint/react";
import * as Sentry from "@sentry/react"; // Import Sentry
import { ensureTrailingSlash } from "./utils/url";
import { getEdgeStartSessionUrl, getEdgeGetSessionUrl, getEdgeRetrieveUrl, isPreviewFrontend, buildEdgeFirstCallPayload } from "./utils/edge";
import { trackEvent } from "./utils/telemetry";
import { postToPublisher } from "./utils/publisherMessage";
import {
  browserName,
  browserVersion,
  fullBrowserVersion,
  osName,
  osVersion,
  deviceType,
  mobileVendor,
  mobileModel,
  getUA
} from "react-device-detect";

// Sentry is currently disabled
// To re-enable, uncomment the Sentry initialization below and the SentryRoutes variable
/*
Sentry.init({
  dsn: "https://e5bbbf0db439485e92518f5484e1baae@o4504792099258368.ingest.us.sentry.io/4504792101158912",
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.breadcrumbsIntegration({
      console: true,
      dom: true,
      fetch: true,
      history: true,
      xhr: true,
    }),
    Sentry.replayIntegration(),
    Sentry.captureConsoleIntegration(),
  ],
  tracesSampleRate: 1.0, // Adjust based on your need
  tracePropagationTargets: [
    /^https:\/\/fe\.dcams\.app/, // Match fe.dcams.app
    /^https:\/\/vx-fe\.dcams\.app/, // Match vx-fe.dcams.app
  ],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});
*/

// const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Function to get request_id from session_id
async function getRequestIdFromSession(session_id) {
  const response = await fetch(
    process.env.REACT_APP_BACKEND_URL + '/v3/stable/api/frontend/exchangeSessionForToken',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ session_id }) // Payload with the session_id
    }
  )

  if (!response.ok) {
    throw new Error(`Error exchanging session: ${response.statusText}`)
  }

  // Sample response: { "request_id": "1f9dfa59-54fa-4067-9a29-26265342b2f2" }
  const result = await response.json()
  return result.request_id
}

// FingerprintJS config helper — used both in imperative early fingerprint and FingerprintProvider
const getFpjsConfig = () => {
  // Same preview detection as the edge API URLs (utils/edge.js) so the
  // FingerprintJS key always matches the backend environment — a prod
  // key on a preview host 403s and takes the whole session load down.
  const isPreviewEnv = isPreviewFrontend() || window.location.hostname.includes("preview");
  const apiKey = isPreviewEnv ? "jnsYBLlwOmn9MDJb0la2" : "U1OoU5FUoWuUW1gJvyL3";
  return {
    apiKey,
    endpoints: "https://dcams.app/uTU6pIMQFwWuQOfC/AWn7OalBYI7aYzip",
  };
};

const AppWithData = () => {
  const { request_id } = useParams();
  const location = useLocation();
  const [sessionData, setSessionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [edgeFingerprintActive, setEdgeFingerprintActive] = useState(null);
  const [edgeFingerprintDone, setEdgeFingerprintDone] = useState(false);
  const [selectedTestCase, setSelectedTestCase] = useState(() => {
    const urlTestCase = new URLSearchParams(window.location.search).get('test_case_id');
    if (urlTestCase) return urlTestCase;
    try { return sessionStorage.getItem('selected_test_case_id') || null; } catch (e) { return null; }
  });
  const [testCaseModalOpen, setTestCaseModalOpen] = useState(false);

  // Persist selected test case to sessionStorage
  useEffect(() => {
    try {
      if (selectedTestCase) {
        sessionStorage.setItem('selected_test_case_id', selectedTestCase);
      } else {
        sessionStorage.removeItem('selected_test_case_id');
      }
    } catch (e) { /* sessionStorage unavailable */ }
  }, [selectedTestCase]);

  // Check fingerprint param directly in the render function to avoid timing issues
  const urlParams = new URLSearchParams(location.search);
  const fingerprintParam = urlParams.get('fingerprint');
  const shouldDisableFingerprint = fingerprintParam === 'f';
  
  // Sentry tag setting is currently disabled
  /*
  useEffect(() => {
    if (request_id) {
      // Set request_id as a tag in Sentry
      Sentry.setTag("request_id", request_id);
    }
  }, [request_id]);
  */

  // Check for frontend query param
  const frontendParam = urlParams.get('frontend');
  const edge = urlParams.get('edge');
  const stateParam = urlParams.get('state');

  // Determine if we should use VX backend based on hostname or query param
  const useVxBackend = window.location.hostname === "vx-fe.dcams.app" || frontendParam === "vx-fe";

  // Check if edge parameter is true OR if request_id/state carries the "-edge" suffix
  const isEdgeFromRequestId = request_id?.endsWith('-edge') || stateParam?.endsWith('-edge');
  const useEdgeEndpoint = edge === "true" || isEdgeFromRequestId;
  
  const vxSessionUrl = ensureTrailingSlash(
    "https://vx.dcams.app/frontend/session/get_session"
  );

  const getPrimaryUrl = useEdgeEndpoint
    ? null // Edge mode uses the get_session first-call POST + GET polling
    : useVxBackend
    ? vxSessionUrl
    : `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/getUrl`;

  const getSecondaryUrl = useEdgeEndpoint
    ? null
    : !useVxBackend
    ? vxSessionUrl
    : `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/getUrl`;

  const edgeDeviceInfo = {
    browserName,
    browserVersion,
    fullBrowserVersion,
    osName,
    osVersion,
    deviceType,
    mobileVendor,
    mobileModel,
    userAgent: getUA
  };

  // Legacy edge path (unchanged): POST start_session with device info, then
  // poll GET get_session. Used when the worker answers the first-call probe
  // with use_legacy (flag off for this request_id) or the probe fails.
  const edgeStartSession = async (requestId) => {
    try {
      const response = await fetch(getEdgeStartSessionUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildEdgeFirstCallPayload({ requestId, deviceInfo: edgeDeviceInfo })),
      });
      if (!response.ok) {
        console.warn(`start_session failed: ${response.status} ${response.statusText}`);
        return null;
      }
      const result = await response.json();
      return {
        requestId: result.request_id || requestId,
        is_fingerprint_active: result.is_fingerprint_active,
      };
    } catch (error) {
      console.warn("start_session error:", error);
      return null;
    }
  };

  // Edge mode: one endpoint, two shapes.
  //  - first call: POST with device_info + url_params (replaces the old
  //    start_session round trip; the worker records device/state then runs
  //    the normal get_session path). The worker answers { use_legacy: true }
  //    without writing anything when enable-get-session-first-call is off
  //    for this request_id; the caller then runs the legacy path.
  //  - later polls: GET /{request_id}?visitor_id=
  // Returns { notReady } until the worker has the session row, and
  // { notReady, fingerprintRequired } when the site has device fraud rules
  // and the worker needs a visitor_id before it will gate the session.
  const edgeGetSession = async (requestId, { visitorId = null, firstCall = false, fingerprintPending = false } = {}) => {
    try {
      let response;
      if (firstCall) {
        response = await fetch(getEdgeGetSessionUrl(), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(buildEdgeFirstCallPayload({
            requestId,
            deviceInfo: edgeDeviceInfo,
            fingerprintPending,
            visitorId,
          })),
        });
      } else {
        let url = `${getEdgeGetSessionUrl()}/${requestId}`;
        if (visitorId) {
          url += `?visitor_id=${encodeURIComponent(visitorId)}`;
        }
        response = await fetch(url, { method: "GET" });
      }

      if (!response.ok) {
        console.warn(`get_session failed: ${response.status}`);
        // A failed first-call POST must be distinguishable from "row not
        // there yet" so the caller can fall back to the legacy path.
        return firstCall ? { postFailed: true, status: response.status } : { notReady: true };
      }

      const result = await response.json();

      // Worker declined the first-call path for this request_id (flag off).
      if (firstCall && result.use_legacy === true) {
        return { useLegacy: true };
      }

      // Check if blocked — must be checked first
      if (result.blocked === true) {
        return { blocked: true };
      }

      // Session not ready yet. Carry the fingerprint hints so the caller
      // can start Fingerprint while the row lands.
      if (result.ready === false) {
        return {
          notReady: true,
          fingerprintRequired: result.fingerprint_required === true,
          isFingerprintActive: result.is_fingerprint_active,
        };
      }

      // Check for already_done
      if (result.already_done === true) {
        return result;
      }

      // Session is ready — must have data
      if (!result.data && result.data !== false && result.already_done !== true) {
        return { notReady: true };
      }

      return result;
    } catch (error) {
      console.warn("get_session error:", error);
      return firstCall ? { postFailed: true } : { notReady: true };
    }
  };

  // Session-load timing. Every mark logs ms since fetchData started and ms
  // since navigation start, and the marks are kept for a summary table at
  // the end. Review in the console under "[EDGE TIMING]".
  const edgeTimingMarks = [];
  let edgeTimingT0 = 0;
  const edgeMark = (label, extra) => {
    const now = performance.now();
    const sinceStart = Math.round(now - edgeTimingT0);
    const sinceNav = Math.round(now);
    edgeTimingMarks.push({ step: label, "+ms": sinceStart, "since nav ms": sinceNav, ...(extra || {}) });
    console.log(`[EDGE TIMING] ${label} +${sinceStart}ms (nav ${sinceNav}ms)`, extra || "");
  };

  // Runs Fingerprint once and returns the visitor_id (null on failure).
  const runEdgeFingerprint = async (requestId) => {
    try {
      console.log("🔑 Running fingerprint before get_session polling...");
      const fpConfig = getFpjsConfig();
      const fp = Fingerprint.start({
        apiKey: fpConfig.apiKey,
        endpoints: fpConfig.endpoints,
      });
      const fpResult = await fp.get({
        tag: { request_id: requestId },
      });
      console.log("🔑 Fingerprint completed, visitor_id:", fpResult.visitor_id);
      edgeMark("fingerprint done", { visitor_id: fpResult.visitor_id || null });
      setEdgeFingerprintDone(true);
      return fpResult.visitor_id || null;
    } catch (fpError) {
      console.warn("🔑 Fingerprint failed (non-blocking):", fpError);
      return null;
    }
  };

  // Non-edge: POST with request_id to get session data
  const fetchAndCheck = async (url, request_id) => {
    try {
      const payload = { request_id };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 404) {
        return { notFound: true };
      }

      if (!response.ok) {
        console.warn(`Error with request to ${url}: ${response.statusText}`);
        return null;
      }

      const result = await response.json();

      // Allow already_done responses to pass through even without data
      if ((!result.data || result.data === false) && result.already_done !== true) {
        return null;
      }

      return result;
    } catch (error) {
      console.warn(`Failed to fetch from ${url}:`, error);
      return null;
    }
  };

  // Fetch the session data first
  useEffect(() => {
    const fetchData = async () => {
      if (!request_id) {
        setIsLoading(false);
        return;
      }

      let localRequestId = request_id;

      // Handle special cases for request_id
      if (localRequestId === 'clear' || localRequestId === 'clear/return' ||
          localRequestId === 'oneid' || localRequestId === 'oneid/return' ||
          localRequestId === 'digilocker' || localRequestId === 'digilocker/return/success' ||
          localRequestId === 'digilocker/return/failure') {
        localRequestId = urlParams.get('state');
        if (localRequestId?.endsWith('-edge')) {
          localRequestId = localRequestId.replace('-edge', '');
        }
      } else if (localRequestId === 'mdl') {
        const session_id = urlParams.get('sessionId');
        if (!session_id) {
          setError('No session_id in query params.');
          setIsLoading(false);
          return;
        }
        try {
          localRequestId = await getRequestIdFromSession(session_id);
        } catch (err) {
          setError('Could not exchange session for token');
          setIsLoading(false);
          return;
        }
      } else if (localRequestId === 'trinsic' || localRequestId === 'bankid-cz' ||
                 localRequestId === 'bankid-se' || localRequestId === 'bankid-no' ||
                 localRequestId === 'finnish-trust-network' || localRequestId === 'mojeid' ||
                 localRequestId === 'brazil-cpf') {
        // Trinsic providers use sessionId to exchange for real request_id
        const session_id = urlParams.get('sessionId');
        if (!session_id) {
          setError('No sessionId in query params for Trinsic return.');
          setIsLoading(false);
          return;
        }
        try {
          localRequestId = await getRequestIdFromSession(session_id);
          // If exchange returns null, call idmax retrieve with sessionId (edge mode)
          if (!localRequestId) {
            console.log('Exchange returned null, calling idmax retrieve with sessionId');
            const resultsAccessKey = urlParams.get('resultsAccessKey');
            const retrieveResponse = await fetch(getEdgeRetrieveUrl(), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ sessionId: session_id, trinsic: true, code: resultsAccessKey }),
            });
            if (retrieveResponse.ok) {
              const retrieveResult = await retrieveResponse.json();
              console.log('IDMax retrieve response:', retrieveResult);
              // Let the window close itself after retrieve completes
              window.close();
              return;
            } else {
              console.error('IDMax retrieve failed:', retrieveResponse.status);
              setError('Could not complete verification');
              setIsLoading(false);
              return;
            }
          }
        } catch (err) {
          setError('Could not exchange session for token (Trinsic)');
          setIsLoading(false);
          return;
        }
      } else if (localRequestId?.endsWith('-edge')) {
        localRequestId = localRequestId.replace('-edge', '');
      }

      let result = null;

      if (useEdgeEndpoint) {
        // Edge mode. The first call is a POST probe on get_session carrying
        // device_info (the old start_session payload). The worker decides the
        // path per request_id from the enable-get-session-first-call flag:
        //   - flag on:  it records device/state and answers like a normal
        //               get_session poll; we keep POSTing until it has the
        //               row, then GET-poll (with visitor_id once Fingerprint
        //               has run), exactly as before the flag existed.
        //   - flag off: it answers { use_legacy: true } with no writes and we
        //               run the legacy start_session -> Fingerprint -> GET
        //               path unchanged.
        // A failed probe (non-2xx, network) also takes the legacy path, so a
        // broken first-call route degrades to today's behaviour by itself.
        const sessionRequestId = localRequestId;
        const maxRetries = 30;
        const retryDelay = 2000;
        // The first not-ready answer usually means the writes consumer has
        // not landed the row yet (p50 1.2s). Legacy's start_session probed
        // every 500ms; waiting a full tick here turned a 0.6s row into a 2s
        // wait. Retry once quickly, then settle to the normal cadence.
        const firstRetryDelay = 750;
        let retryCount = 0;
        let edgeVisitorId = null;
        let fingerprintRan = false;
        let useFirstCall = true;
        let legacyPath = false;

        edgeTimingT0 = performance.now();
        edgeMark("fetchData start");

        {
          const callStart = performance.now();
          result = await edgeGetSession(sessionRequestId, {
            firstCall: true,
            fingerprintPending: !shouldDisableFingerprint,
          });
          edgeMark("POST first-call probe response", {
            rtt_ms: Math.round(performance.now() - callStart),
            outcome: !result ? "error" : result.useLegacy ? "use_legacy" : result.postFailed ? `post_failed_${result.status || "network"}` : result.notReady ? "not_ready" : "ready",
          });
          if (!result || result.postFailed || result.useLegacy) {
            legacyPath = true;
            trackEvent("edge_session_path", {
              path: "legacy",
              reason: result?.useLegacy ? "flag_off" : result?.status ? `post_${result.status}` : "post_error",
            });
          } else {
            trackEvent("edge_session_path", { path: "first_call" });
          }
        }

        if (legacyPath) {
          const startResult = await edgeStartSession(sessionRequestId);
          if (!startResult) {
            console.error("Failed to start edge session");
            setError("Failed to load session data");
            setIsLoading(false);
            return;
          }
          if (startResult.is_fingerprint_active !== undefined) {
            setEdgeFingerprintActive(startResult.is_fingerprint_active);
          }
          // Legacy order: Fingerprint before the first GET so the worker has
          // a visitor_id before it decides to block.
          if (startResult.is_fingerprint_active === true && !shouldDisableFingerprint) {
            fingerprintRan = true;
            edgeMark("fingerprint start (legacy)");
            edgeVisitorId = await runEdgeFingerprint(sessionRequestId);
          }
          useFirstCall = false;
          result = null;
        }

        while (retryCount < maxRetries && (!result || result.notReady)) {
          if (result?.fingerprintRequired && !fingerprintRan && !shouldDisableFingerprint) {
            // Worker has the row but wants a visitor_id before gating it.
            // No delay: it is ready, it just needs the id.
            fingerprintRan = true;
            useFirstCall = false;
            edgeMark("fingerprint start");
            edgeVisitorId = await runEdgeFingerprint(sessionRequestId);
          } else if (result) {
            await new Promise(resolve => setTimeout(resolve, retryCount === 0 ? firstRetryDelay : retryDelay));
          }
          retryCount++;
          const callStart = performance.now();
          const callKind = useFirstCall ? "POST first-call" : `GET poll${edgeVisitorId ? " +visitor_id" : ""}`;
          result = await edgeGetSession(sessionRequestId, {
            visitorId: edgeVisitorId,
            firstCall: useFirstCall,
            fingerprintPending: useFirstCall && !fingerprintRan && !shouldDisableFingerprint,
          });
          // A transient failure on a repeat POST is just "not ready yet".
          if (result?.postFailed) result = { notReady: true };
          edgeMark(`${callKind} #${retryCount} response`, {
            rtt_ms: Math.round(performance.now() - callStart),
            outcome: !result ? "error" : result.notReady ? (result.fingerprintRequired ? "fingerprint_required" : "not_ready") : (result.blocked ? "blocked" : result.already_done ? "already_done" : "ready"),
          });
          if (result?.isFingerprintActive !== undefined) {
            setEdgeFingerprintActive(result.isFingerprintActive);
          }
          if (result?.fingerprintRequired) {
            useFirstCall = false;
          }
        }

        edgeMark("session load finished", { polls: retryCount + 1, fingerprint_ran: fingerprintRan, path: legacyPath ? "legacy" : "first_call" });
        console.log("[EDGE TIMING] summary");
        console.table(edgeTimingMarks);

        if (!result || result.notReady) {
          console.error("Edge session not ready after polling. result:", result);
          setError("Failed to load session data");
          setIsLoading(false);
          return;
        }

        if (result?.data?.settings?.is_fingerprint_active !== undefined) {
          setEdgeFingerprintActive(result.data.settings.is_fingerprint_active);
        }

        // Check if blocked by server
        if (result?.blocked) {
          setError("BLOCKED");
          setIsLoading(false);
          return;
        }
      } else {
        // Non-edge mode: POST to primary/secondary URLs
        const maxRetries = 3;
        let retryCount = 0;

        while (retryCount < maxRetries) {
          result = await fetchAndCheck(getPrimaryUrl, localRequestId);

          if (result && !result.notFound) {
            break;
          }

          // If primary failed, try secondary
          console.warn(`Primary URL failed (attempt ${retryCount + 1}/${maxRetries}), trying secondary URL: ${getSecondaryUrl}`);
          result = await fetchAndCheck(getSecondaryUrl, localRequestId);

          if (result && !result.notFound) {
            break;
          }

          retryCount++;
        }

        if (!result || result.notFound) {
          console.error("Failed to fetch data from both primary and secondary URLs after retries. result:", result);
          setError("Failed to load session data");
          setIsLoading(false);
          return;
        }
      }


      // Check if session is already done.
      // Passkey-setup popup exception: when we opened this window as a
      // top-level popup specifically for WebAuthn create() in a completed
      // session, we *want* the data block to pass through to DataProvider
      // so the popup can read settings.passkey.session_token. Skip the
      // ALREADY_DONE short-circuit in that mode.
      const isPasskeySetupPopup =
        typeof window !== "undefined" &&
        new URLSearchParams(window.location.search).get("passkey_setup") === "1";
      if (result.already_done === true && !isPasskeySetupPopup) {
        setError("ALREADY_DONE");
        setIsLoading(false);
        return;
      }

      setSessionData(result);
      setIsLoading(false);
    };

    fetchData();
  }, [request_id]);

  // Send postMessage for ALREADY_DONE - must be before any conditional returns
  React.useEffect(() => {
    if (error === "ALREADY_DONE") {

      // Send payload version
      const payload = {
        messageType: "payload",
        value: {
          event: "ERROR",
          app: null,
          error: "ALREADY DONE"
        }
      };
      postToPublisher(payload, { target: "parent", trigger: "already_done" });

      // Send simple string version
      postToPublisher("ALREADY DONE", { target: "parent", trigger: "already_done" });
    }
  }, [error]);

  if (isLoading) {
    // Was a plain LoadingOverlay backdrop — now the branded InitialLoadingScreen
    // so the very first paint reads as "your verification is starting" with
    // the publisher's primary color, instead of an empty gray overlay.
    return <InitialLoadingScreen />;
  }

  if (error) {
    if (error === "BLOCKED") {
      const isIframe = window.self !== window.top;
      return (
        <Box style={{ position: 'relative', height: '100vh' }}>
          {isIframe && (
            <Box
              style={{
                position: 'fixed',
                top: 0,
                insetInlineEnd: 0,
                padding: '8px 12px',
                zIndex: 1000,
              }}
            >
              <ActionIcon
                variant="light"
                onClick={() => postToPublisher("CLOSE", { target: "parent", trigger: "blocked_close" })}
              >
                <IconX stroke={1.5} size={18} />
              </ActionIcon>
            </Box>
          )}
          <Center style={{ height: '100%', flexDirection: 'column', padding: '20px' }}>
            <Stack align="center" spacing="md">
              <ThemeIcon variant="light" color="red" size={80} radius="xl">
                <IconShieldX size={50} stroke={1.5} />
              </ThemeIcon>
              <Text size="xl" weight={700} align="center">
                Unable to Continue
              </Text>
            </Stack>
          </Center>
        </Box>
      );
    }

    const errorTitle = error === "ALREADY_DONE" ? "Link Expired" : "Error";
    const errorMessage = error === "ALREADY_DONE"
      ? "This link is either invalid, expired, or has already been used"
      : error;

    return (
      <Center style={{ height: '100vh', flexDirection: 'column', padding: '20px' }}>
        <Text color="red" size="xl" mb={10}>❌</Text>
        <Text size="xl" weight={700} mb={10}>{errorTitle}</Text>
        <Text align="center">{errorMessage}</Text>
      </Center>
    );
  }

  // Determine if fingerprinting should be disabled based on URL param, settings, or the edge first call
  // If early fingerprint was already done (edge mode), skip FpjsProvider entirely
  const isFingerPrintActive = edgeFingerprintActive !== null
    ? edgeFingerprintActive !== false
    : sessionData?.data?.settings?.is_fingerprint_active !== false;
  const fingerprintDisabled = shouldDisableFingerprint || !isFingerPrintActive || edgeFingerprintDone;

  // Check for test mode from session data (edge mode)
  const isTestMode = sessionData?.test_mode === true;
  const testCases = sessionData?.test_cases || [];

  // Get the selected test case name for display
  const selectedTestCaseName = testCases.find((tc) => tc.id === selectedTestCase)?.test_case_name;

  // Test mode banner component
  const TestModeBanner = () => {
    const hasTestCases = testCases.length > 0;

    return (
      <>
        <div
          style={{
            position: 'fixed',
            top: 0,
            insetInlineStart: 0,
            insetInlineEnd: 0,
            backgroundColor: '#ff9800',
            color: '#000',
            textAlign: 'center',
            padding: '4px 8px',
            fontSize: '11px',
            fontWeight: 'bold',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            flexWrap: 'wrap',
          }}
        >
          <span>Test Mode Active</span>
          {hasTestCases ? (
            <>
              <button
                onClick={() => setTestCaseModalOpen(true)}
                style={{
                  fontSize: '11px',
                  padding: '1px 8px',
                  borderRadius: '3px',
                  border: '1px solid #e65100',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                }}
              >
                {selectedTestCaseName || 'Select Test Case'}
              </button>
              {!selectedTestCase && (
                <span style={{ fontSize: '10px', fontWeight: 'normal', fontStyle: 'italic' }}>
                  No test case selected — will hit live services
                </span>
              )}
            </>
          ) : (
            <span style={{ fontSize: '10px', fontWeight: 'normal', fontStyle: 'italic' }}>
              No test cases configured — add test cases in the admin portal
            </span>
          )}
        </div>

        <Modal
          opened={testCaseModalOpen}
          onClose={() => setTestCaseModalOpen(false)}
          title="Select Test Case"
          centered
          zIndex={100000}
          size="sm"
        >
          <Text size="sm" color="dimmed" mb="md">
            Choose a test case to simulate, or clear your selection to hit live services.
          </Text>
          <Radio.Group
            value={selectedTestCase || ''}
            onChange={(value) => {
              setSelectedTestCase(value || null);
              setTestCaseModalOpen(false);
            }}
          >
            <Stack spacing="xs">
              {testCases.map((tc) => (
                <Radio key={tc.id} value={tc.id} label={tc.test_case_name} />
              ))}
            </Stack>
          </Radio.Group>
          {selectedTestCase && (
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              mt="md"
              fullWidth
              onClick={() => {
                setSelectedTestCase(null);
                setTestCaseModalOpen(false);
              }}
            >
              Clear selection (use live services)
            </Button>
          )}
        </Modal>
      </>
    );
  };

  // Merge selected test case into session data for downstream consumption
  const enrichedSessionData = sessionData
    ? { ...sessionData, selected_test_case_id: selectedTestCase }
    : sessionData;

  // Skip FpjsProvider when disabled via URL param or settings
  if (fingerprintDisabled) {
    return (
      <>
        {isTestMode && <TestModeBanner />}
        <DataProvider
          request_id={request_id}
          fingerprintDisabled={true}
          initialSessionData={enrichedSessionData}
          selectedTestCaseId={selectedTestCase}
        >
          <App routes={<SubRoutes />} />
        </DataProvider>
      </>
    );
  }

  // Regular flow with fingerprinting (non-edge, or edge without early fingerprint)
  const fpConfig = getFpjsConfig();

  return (
    <>
      {isTestMode && <TestModeBanner />}
      <FingerprintProvider
        apiKey={fpConfig.apiKey}
        endpoints={fpConfig.endpoints}
      >
        <DataProvider
          request_id={request_id}
          fingerprintDisabled={false}
          initialSessionData={enrichedSessionData}
          selectedTestCaseId={selectedTestCase}
        >
          <App routes={<SubRoutes />} />
        </DataProvider>
      </FingerprintProvider>
    </>
  );
};

// Legacy capture links are `/?request_id=<id>` (the old frontend.dcams.app app).
// This app routes on the path (`/:request_id`), so send those links to
// `/<id>`, keeping every other query parameter (language, edge, ...).
// Anything else at `/` is still Not Found.
export const legacyQueryTarget = (search) => {
  const params = new URLSearchParams(search || "");
  const id = (params.get("request_id") || "").trim();
  if (!/^[A-Za-z0-9_-]{8,64}$/.test(id)) return null;
  params.delete("request_id");
  const rest = params.toString();
  return `/${id}${rest ? `?${rest}` : ""}`;
};

const LegacyQueryRedirect = () => {
  const { search } = useLocation();
  const target = legacyQueryTarget(search);
  return target ? <Navigate to={target} replace /> : <NotFound />;
};

const RootComponent = () => {
  // Read colorScheme, primaryColor, and backgroundColor from URL immediately for
  // initial render — so the page paints with the customer's chrome before data
  // loads from the API.
  const urlParams = new URLSearchParams(window.location.search);
  const colorScheme = urlParams.get('colorScheme') || 'light';
  const primaryColor = urlParams.get('primaryColor');
  const rawBackgroundColor = urlParams.get('backgroundColor');
  const backgroundColor = rawBackgroundColor
    ? (rawBackgroundColor.startsWith('#') ? rawBackgroundColor : `#${rawBackgroundColor}`)
    : null;

  // Build theme object — include primaryColor as a custom shade if provided.
  // LoadingOverlay backdrop is opaque and matches the app body color so the
  // "locked" visual cue is preserved without tinting a customer background.
  const themeOverrides = {
    colorScheme,
    components: {
      LoadingOverlay: {
        defaultProps: (theme) => ({
          overlayColor:
            backgroundColor ??
            (theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white),
          overlayOpacity: 1,
        }),
      },
    },
  };
  if (primaryColor) {
    try {
      const hex = primaryColor.startsWith('#') ? primaryColor : `#${primaryColor}`;
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      // Mix with white (lighter) or black (darker) to create 10 shades
      const mix = (c, target, weight) => Math.round(c + (target - c) * weight);
      const toHex = (r, g, b) =>
        `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
      const weights = [0.85, 0.7, 0.55, 0.4, 0.25, 0.1, 0, 0.15, 0.3, 0.45];
      const shades = weights.map((w, i) =>
        i <= 5
          ? toHex(mix(r, 255, w), mix(g, 255, w), mix(b, 255, w))
          : toHex(mix(r, 0, w), mix(g, 0, w), mix(b, 0, w))
      );
      themeOverrides.colors = { brand: shades };
      themeOverrides.primaryColor = 'brand';
    } catch (e) {
      // Invalid color format — fall back to default theme
    }
  }

  return (
    <MantineProvider
      theme={themeOverrides}
      withGlobalStyles
      withNormalizeCSS
    >
      <Global
        styles={(theme) => ({
          body: {
            backgroundColor:
              backgroundColor ||
              (theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white),
          },
        })}
      />
      <Router>
        {/* Sentry error boundary is currently disabled
        <Sentry.ErrorBoundary fallback={<div>An error has occurred.</div>}>
        */}
          <Routes>
            <Route path="/" element={<LegacyQueryRedirect />} />
            <Route path="/:request_id/*" element={<AppWithData />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        {/* </Sentry.ErrorBoundary> */}
      </Router>
    </MantineProvider>
  );
};

export default RootComponent;
